import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCredit } from '../../contexts/CreditContext';
import * as usageService from '../../services/usageService';
import * as subscriptionService from '../../services/subscriptionService';
import { useUsageData } from '../../hooks/useUsage';
import UsageSkeleton from '../../components/skeletons/UsageSkeleton';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  CardActions,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  Snackbar
} from '@mui/material';
import {
  TrendingUp,
  Assessment,
  CreditCard,
  Receipt,
  Cancel,
  CheckCircle,
  Warning,
  Schedule,
  Star,
  Diamond,
  Speed,
  Refresh,
  Add,
  Info,
  CalendarToday,
  Autorenew,
  Close,
  ChatBubble as ChatBubbleIcon,
  Assessment as AssessmentIcon,
  QrCode2 as QrCode2Icon,
  CreditCard as CreditCardIcon,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import ProfessionalSubscriptionPopup from '../../components/ProfessionalSubscriptionPopup';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

export const UsagePage: React.FC = () => {
  const { user } = useAuth();
  const { currentSubscription } = useCredit();
  const navigate = useNavigate();
  
  // ⚡ React Query com cache e auto-refetch
  const { data: usageData, isLoading: loading, error: usageError } = useUsageData();
  const error = usageError ? 'Erro ao carregar dados de consumo. Por favor, tente novamente mais tarde.' : null;
  
  // Estados locais da UI
  const [tabValue, setTabValue] = useState(0);
  const [showLast10, setShowLast10] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPlansPopup, setShowPlansPopup] = useState(false);
  
  // Fallback para dados vazios
  const safeUsageData = usageData || {
    total_tokens: 0,
    total_documents: 0,
    available_tokens: 0,
    tokens_per_credit: 2000,
    chat_history: [],
    document_history: [],
    payment_history: []
  };

  // ⚡ REMOVIDO: useEffect para carregar dados
  // React Query faz isso automaticamente com refetch a cada 1 minuto

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      const result = await subscriptionService.cancelSubscription();
      
      if (result.success) {
        setCancelSuccess(result.message);
        setCancelDialogOpen(false);
        // Recarregar dados após cancelamento
        window.location.reload();
      } else {
        setCancelError('Erro ao cancelar assinatura');
      }
    } catch (err: any) {
      console.error('Error canceling subscription:', err);
      setCancelError(err.response?.data?.detail || 'Erro ao cancelar assinatura');
    } finally {
      setCancelLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'cancelled':
        return <Cancel sx={{ color: 'error.main' }} />;
      case 'pending':
        return <Schedule sx={{ color: 'warning.main' }} />;
      default:
        return <Warning sx={{ color: 'grey.500' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatPaymentMethod = (method: string, transactionType?: string) => {
    if (transactionType === 'subscription') {
      return 'Assinatura';
    }
    
    switch (method) {
      case 'credit':
        return 'Cartão de Crédito';
      case 'pix':
        return 'PIX';
      case 'debit':
        return 'Cartão de Débito';
      default:
        return method;
    }
  };

  const formatBRL = (value?: number) =>
    (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor(diff / (1000 * 60));
    
    if (days > 0) {
      return `${days} dia${days === 1 ? '' : 's'} atrás`;
    } else if (hours > 0) {
      return `${hours} hora${hours === 1 ? '' : 's'} atrás`;
    } else if (minutes > 0) {
      return `${minutes} minuto${minutes === 1 ? '' : 's'} atrás`;
    } else {
      return 'Agora mesmo';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPaymentStatusText = (status: string) => {
    switch (status) {
      case 'paid':
        return 'Pago';
      case 'completed':
        return 'Concluído';
      case 'pending':
        return 'Pendente';
      case 'failed':
        return 'Falhou';
      case 'cancelled':
        return 'Cancelado';
      default:
        return status;
    }
  };

  // Função para abrir o popup de planos
  const handleOpenPlansPopup = () => {
    setShowPlansPopup(true);
  };

  // Função para fechar o popup de planos
  const handleClosePlansPopup = () => {
    setShowPlansPopup(false);
  };

  // ⚡ Skeleton Screen - mostra estrutura instantaneamente
  if (loading) {
    return <UsageSkeleton />;
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1">
          Meu Consumo
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenPlansPopup}
        >
          Adicionar Saldo
        </Button>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Plano e Saldo - Movido para o início */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Plano e Saldo
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                {currentSubscription && currentSubscription.plan ? (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ mr: 2 }}>
                        Plano Atual
                      </Typography>
                      {getStatusIcon(currentSubscription.status)}
                      <Chip 
                        label={currentSubscription.status === 'active' ? 'Ativo' : 'Cancelado'}
                        color={getStatusColor(currentSubscription.status) as any}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    
                    <Typography variant="h4" color="primary" gutterBottom>
                      {currentSubscription.plan?.name || 'Plano Desconhecido'}
                    </Typography>
                    
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {formatBRL(currentSubscription.amount)}/mês
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {currentSubscription.plan?.description || 'Sem descrição'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Star sx={{ color: 'gold', mr: 1 }} />
                      <Typography variant="body2">
                        {currentSubscription.tokens_included?.toLocaleString() || 0} tokens inclusos
                      </Typography>
                    </Box>
                    
                    {currentSubscription.next_billing_date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CalendarToday sx={{ color: 'text.secondary', mr: 1 }} />
                        <Typography variant="body2">
                          {new Date(currentSubscription.next_billing_date) < new Date() ? 'Cobrança prevista em' : 'Próxima cobrança'}: {new Date(currentSubscription.next_billing_date).toLocaleDateString('pt-BR')}
                        </Typography>
                      </Box>
                    )}
                    
                    {currentSubscription.auto_renew && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Autorenew sx={{ color: 'success.main', mr: 1 }} />
                        <Typography variant="body2" color="success.main">
                          Renovação automática ativada
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => setShowPlansPopup(true)}
                        sx={{
                          borderColor: '#1E4562',
                          color: '#1E4562',
                          '&:hover': {
                            borderColor: '#0F2A44',
                            bgcolor: 'rgba(30, 69, 98, 0.05)'
                          }
                        }}
                      >
                        Alterar Plano
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => setCancelDialogOpen(true)}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ mr: 2 }}>
                        Plano Atual
                      </Typography>
                      <Chip 
                        label="Gratuito"
                        color="default"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                    
                    <Typography variant="h4" color="primary" gutterBottom>
                      Plano Gratuito
                    </Typography>
                    
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      R$ 0,00/mês
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" paragraph>
                      Plano básico com recursos limitados
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Star sx={{ color: 'gold', mr: 1 }} />
                      <Typography variant="body2">
                        Créditos limitados
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                      <Button
                        variant="contained"
                        onClick={() => setShowPlansPopup(true)}
                        sx={{
                          bgcolor: '#1E4562',
                          '&:hover': {
                            bgcolor: '#0F2A44'
                          }
                        }}
                      >
                        Assinar Plano
                      </Button>
                    </Box>
                  </>
                )}
              </CardContent>
              
              <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                {/* Removed the "Adicionar Saldo" button from here as it's now in the header */}
                {currentSubscription && currentSubscription.plan && currentSubscription.plan.id !== 'basic' && currentSubscription.status === 'active' && currentSubscription.auto_renew && (
                  <Button 
                    variant="outlined" 
                    color="error" 
                    startIcon={<Cancel />}
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    Cancelar Assinatura
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Saldo Atual
                </Typography>
                <Typography variant="h4" color="primary" gutterBottom>
                  {safeUsageData?.available_tokens || 0} créditos
                </Typography>
                <Typography variant="body1" paragraph>
                  Adicione saldo para continuar utilizando nossos serviços
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleOpenPlansPopup}
                  sx={{ mt: 2 }}
                >
                  Adicionar Saldo
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Usage Statistics */}
      <Typography variant="h5" gutterBottom>
        Estatísticas de Uso
      </Typography>
      <Box sx={{ mb: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Taxa de conversão: <strong>1 crédito</strong> para cada <strong>{safeUsageData?.tokens_per_credit || 20} tokens</strong> utilizados.
          </Typography>
        </Alert>
      </Box>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total de Tokens Utilizados
              </Typography>
              <Typography variant="h3">
                {(safeUsageData?.total_tokens || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Documentos Gerados
              </Typography>
              <Typography variant="h3">
                {(safeUsageData?.total_documents || 0).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Usage Graphs */}
      <Box sx={{ mb: 4 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Histórico de Pagamentos" {...a11yProps(0)} />
          <Tab label="Histórico de Chat" {...a11yProps(1)} />
          <Tab label="Histórico de Documentos" {...a11yProps(2)} />
        </Tabs>
        
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Histórico de Pagamentos
          </Typography>
          {(!safeUsageData?.payment_history || safeUsageData.payment_history.length === 0) ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <CreditCardIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum pagamento realizado ainda
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Adicione créditos ou assine um plano para ver o histórico aqui
              </Typography>
              <Button 
                variant="contained" 
                sx={{ mt: 2 }} 
                onClick={handleOpenPlansPopup}
              >
                Adicionar Créditos
              </Button>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Transação</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {safeUsageData.payment_history.slice(0, showLast10 ? 10 : safeUsageData.payment_history.length).map((payment: any, index: number) => (
                    <TableRow key={payment.id || index} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {payment.transaction_type === 'subscription' ? (
                            <Autorenew sx={{ mr: 1, color: 'success.main' }} />
                          ) : payment.payment_method === 'credit' ? (
                            <CreditCardIcon sx={{ mr: 1, color: 'primary.main' }} />
                          ) : (
                            <QrCode2Icon sx={{ mr: 1, color: 'secondary.main' }} />
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {payment.transaction_type === 'subscription' 
                                ? `Plano ${payment.subscription_plan || 'Profissional'}`
                                : 'Compra de Créditos'
                              }
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {payment.transaction_type === 'subscription' 
                                ? `Assinatura mensal${payment.tokens_included ? ` - ${payment.tokens_included.toLocaleString()} tokens` : ''}`
                                : `${formatPaymentMethod(payment.payment_method, payment.transaction_type)}${payment.credits_added ? ` - ${payment.credits_added} créditos` : ''}`
                              }
                            </Typography>
                            {payment.card_last_digits && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                **** {payment.card_last_digits}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {payment.transaction_type === 'subscription' 
                            ? `Acesso ao plano ${payment.subscription_plan || 'Profissional'}${payment.tokens_included ? ` (${payment.tokens_included.toLocaleString()} tokens)` : ''}`
                            : payment.credits_added 
                              ? `${payment.credits_added} créditos adicionados`
                              : 'Compra de créditos'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(payment.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatBRL(payment.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getPaymentStatusText(payment.status)} 
                          color={getPaymentStatusColor(payment.status) as any}
                          size="small"
                          variant="filled"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {(safeUsageData?.payment_history?.length || 0) > 10 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="outlined" 
                onClick={() => setShowLast10(!showLast10)}
                startIcon={showLast10 ? <ExpandMore /> : <ExpandLess />}
              >
                {showLast10 ? `Mostrar Todos (${safeUsageData.payment_history?.length ?? 0})` : 'Mostrar Últimos 10'}
              </Button>
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Histórico de Conversas com IA
          </Typography>
          {(!safeUsageData?.chat_history || safeUsageData.chat_history.length === 0) ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <ChatBubbleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhuma conversa ainda
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Comece uma conversa com a IA para ver o histórico aqui
              </Typography>
              <Button 
                variant="contained" 
                sx={{ mt: 2 }} 
                onClick={() => navigate('/chat')}
              >
                Iniciar Conversa
              </Button>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Conversa</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Tokens</TableCell>
                    <TableCell>Provedor</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {safeUsageData.chat_history.slice(0, showLast10 ? 10 : safeUsageData.chat_history.length).map((chat: any, index: number) => (
                    <TableRow key={chat.id || index} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <ChatBubbleIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">
                            {chat.session_title || 'Conversa'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(chat.created_at || chat.date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${(chat.tokens_used || chat.tokensUsed || 0).toLocaleString()} tokens`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={chat.provider || 'OpenAI'}
                          size="small"
                          color="default"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {(safeUsageData?.chat_history?.length || 0) > 10 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="outlined" 
                onClick={() => setShowLast10(!showLast10)}
                startIcon={showLast10 ? <ExpandMore /> : <ExpandLess />}
              >
                {showLast10 ? `Mostrar Todos (${safeUsageData.chat_history?.length ?? 0})` : 'Mostrar Últimos 10'}
              </Button>
            </Box>
          )}
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            Histórico de Documentos Gerados
          </Typography>
          {(!safeUsageData?.document_history || safeUsageData.document_history.length === 0) ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <AssessmentIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum documento gerado ainda
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Crie documentos usando nossos templates para ver o histórico aqui
              </Typography>
              <Button 
                variant="contained" 
                sx={{ mt: 2 }} 
                onClick={() => navigate('/documents')}
              >
                Criar Documento
              </Button>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Documento</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell align="right">Tokens</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {safeUsageData.document_history.slice(0, showLast10 ? 10 : safeUsageData.document_history.length).map((doc: any, index: number) => (
                    <TableRow key={doc.id || index} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <AssessmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">
                            {doc.document_type || doc.type || 'Documento'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(doc.created_at || doc.date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Chip 
                          label={`${(doc.tokens_used || doc.tokensUsed || 0).toLocaleString()} tokens`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {(safeUsageData?.document_history?.length || 0) > 10 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Button 
                variant="outlined" 
                onClick={() => setShowLast10(!showLast10)}
                startIcon={showLast10 ? <ExpandMore /> : <ExpandLess />}
              >
                {showLast10 ? `Mostrar Todos (${safeUsageData.document_history?.length ?? 0})` : 'Mostrar Últimos 10'}
              </Button>
            </Box>
          )}
        </TabPanel>
      </Box>

      {/* Dialog para cancelar assinatura */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>Cancelar Assinatura</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja cancelar sua assinatura? Você perderá o acesso aos benefícios do plano.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Manter Assinatura</Button>
          <Button 
            onClick={handleCancelSubscription} 
            color="error" 
            variant="contained"
            disabled={cancelLoading}
          >
            {cancelLoading ? <CircularProgress size={20} /> : 'Cancelar Assinatura'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popup de planos */}
      <ProfessionalSubscriptionPopup
        open={showPlansPopup}
        onClose={handleClosePlansPopup}
      />

      {/* Snackbar para mensagem de sucesso */}
      {cancelSuccess && (
        <Snackbar
          open={!!cancelSuccess}
          autoHideDuration={6000}
          onClose={() => setCancelSuccess(null)}
        >
          <Alert onClose={() => setCancelSuccess(null)} severity="success">
            {cancelSuccess}
          </Alert>
        </Snackbar>
      )}
    </Container>
  );
};

export default UsagePage; 